import React, { useState, useMemo, useEffect } from 'react';
import host from '../../assets/js/Host';

const API_URL = `${host}users/calls/logs`;

function StatCard({ title, value, icon, color, subtitle }) {
  return (
    <div style={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '12px',
      padding: '20px',
      background: '#fff',
      border: `2px solid ${color}30`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
    }}>
      <div style={{ position: 'absolute', top: '-8px', left: '-8px', fontSize: '50px', opacity: 0.1 }}>{icon}</div>
      <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '6px' }}>{title}</div>
      <div style={{ fontSize: '28px', fontWeight: '700', color }}>{value}</div>
      {subtitle && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{subtitle}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    'تأكيد': { bg: '#dcfce7', color: '#16a34a' },
    'الغاء': { bg: '#fee2e2', color: '#dc2626' },
    'default': { bg: '#fef3c7', color: '#d97706' }
  };
  const s = styles[status] || styles.default;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
}

function LoadingSpinner() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px' }}>
      <div style={{
        width: '50px',
        height: '50px',
        border: '4px solid #e2e8f0',
        borderTop: '4px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <p style={{ marginTop: '16px', color: '#64748b' }}>جاري تحميل البيانات...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorMessage({ message, onRetry }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px', background: '#fef2f2', borderRadius: '12px', margin: '20px 0' }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
      <h3 style={{ color: '#dc2626', margin: '0 0 8px' }}>خطأ في تحميل البيانات</h3>
      <p style={{ color: '#64748b', margin: '0 0 16px' }}>{message}</p>
      <button onClick={onRetry} style={{
        background: '#6366f1',
        color: 'white',
        border: 'none',
        padding: '10px 24px',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600'
      }}>
        إعادة المحاولة
      </button>
    </div>
  );
}

// تبويب سجل التأكيد
function LogsTab({ logs, loading }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUser, setFilterUser] = useState('all');

  const users = useMemo(() => {
    const uniqueUsers = {};
    logs.forEach(log => {
      if (log.user?.id) uniqueUsers[log.user.id] = log.user.name;
    });
    return uniqueUsers;
  }, [logs]);

  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      if (filterStatus !== 'all' && log.status !== filterStatus) return false;
      if (filterUser !== 'all' && log.user?.id !== parseInt(filterUser)) return false;
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchName = log.order?.name?.toLowerCase().includes(search);
        const matchPhone = log.order?.phone?.includes(search);
        const matchId = log.order?.id?.toString().includes(search);
        if (!matchName && !matchPhone && !matchId) return false;
      }
      return true;
    });
  }, [logs, filterStatus, filterUser, searchTerm]);

  const fmt = d => new Date(d).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const fmtDate = d => new Date(d).toLocaleDateString('en-GB');

  const th = { padding: '14px 12px', textAlign: 'right', fontWeight: '600', fontSize: '13px', color: '#475569', borderBottom: '2px solid #e2e8f0' };
  const td = { padding: '14px 12px', textAlign: 'right', fontSize: '13px', borderBottom: '1px solid #f1f5f9' };

  return (
    <div>
      {/* Filters */}
      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: '200px' }}>
          <span style={{ fontSize: '18px' }}>🔍</span>
          <input
            type="text"
            placeholder="بحث بالاسم، الهاتف، أو رقم الطلب..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{
              flex: 1,
              padding: '10px 14px',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              background: '#fff'
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#64748b', fontSize: '13px' }}>الحالة:</label>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#fff' }}
          >
            <option value="all">الكل</option>
            <option value="تأكيد">تأكيد</option>
            <option value="الغاء">الغاء</option>
            <option value="لا يرد عند تأكيد">لا يرد</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#64748b', fontSize: '13px' }}>المستخدم:</label>
          <select
            value={filterUser}
            onChange={e => setFilterUser(e.target.value)}
            style={{ padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#fff' }}
          >
            <option value="all">الكل</option>
            {Object.entries(users).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>
        <span style={{ color: '#94a3b8', fontSize: '13px' }}>
          عرض {filteredLogs.length} من {logs.length}
        </span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        {loading ? (
          <LoadingSpinner />
        ) : filteredLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
            <p>لا توجد سجلات</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={th}>#</th>
                  <th style={th}>التاريخ</th>
                  <th style={th}>الوقت</th>
                  <th style={th}>المستخدم</th>
                  <th style={th}>رقم الطلب</th>
                  <th style={th}>اسم الزبون</th>
                  <th style={th}>الهاتف</th>
                  <th style={th}>المحافظة</th>
                  <th style={th}>الحالة</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.slice(0, 100).map((log, i) => (
                  <tr key={log.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={td}>{i + 1}</td>
                    <td style={td}>{fmtDate(log.createdAt)}</td>
                    <td style={td}>{fmt(log.createdAt)}</td>
                    <td style={td}>{log.user?.name || '-'}</td>
                    <td style={td}>#{log.order?.id}</td>
                    <td style={td}>{log.order?.name || '-'}</td>
                    <td style={{ ...td, direction: 'ltr', textAlign: 'right' }}>{log.order?.phone || '-'}</td>
                    <td style={td}>{log.order?.government || '-'}</td>
                    <td style={td}><StatusBadge status={log.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredLogs.length > 100 && (
              <div style={{ padding: '16px', textAlign: 'center', color: '#64748b', background: '#f8fafc' }}>
                يتم عرض أول 100 سجل من {filteredLogs.length}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// تبويب التقارير
function ReportsTab({ logs, loading }) {
  const [filterUser, setFilterUser] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  // حساب معدلات الحالات
  const statusStats = useMemo(() => {
    const total = logs.length;
    const confirmed = logs.filter(l => l.status === 'تأكيد').length;
    const cancelled = logs.filter(l => l.status === 'الغاء').length;
    const noAnswer = logs.filter(l => l.status === 'لا يرد عند تأكيد').length;
    
    return {
      total,
      confirmed,
      cancelled,
      noAnswer,
      confirmedPct: total > 0 ? ((confirmed / total) * 100).toFixed(1) : 0,
      cancelledPct: total > 0 ? ((cancelled / total) * 100).toFixed(1) : 0,
      noAnswerPct: total > 0 ? ((noAnswer / total) * 100).toFixed(1) : 0,
    };
  }, [logs]);

  const analysis = useMemo(() => {
    if (!logs.length) return { suspiciousCalls: [], userStats: {} };

    const userGroups = {};
    logs.forEach(log => {
      const uid = log.user?.id;
      if (!uid) return;
      if (!userGroups[uid]) userGroups[uid] = { name: log.user.name, logs: [] };
      userGroups[uid].logs.push(log);
    });

    const suspiciousCalls = [];
    const userStats = {};

    Object.entries(userGroups).forEach(([uid, data]) => {
      const sorted = [...data.logs].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      
      // حساب الحالات لكل مستخدم
      const userConfirmed = data.logs.filter(l => l.status === 'تأكيد').length;
      const userCancelled = data.logs.filter(l => l.status === 'الغاء').length;
      const userNoAnswer = data.logs.filter(l => l.status === 'لا يرد عند تأكيد').length;
      
      userStats[uid] = { 
        name: data.name, 
        total: sorted.length, 
        suspicious: 0, 
        critical: 0, 
        high: 0, 
        medium: 0,
        confirmed: userConfirmed,
        cancelled: userCancelled,
        noAnswer: userNoAnswer,
        confirmedPct: sorted.length > 0 ? ((userConfirmed / sorted.length) * 100).toFixed(1) : 0,
        cancelledPct: sorted.length > 0 ? ((userCancelled / sorted.length) * 100).toFixed(1) : 0,
        noAnswerPct: sorted.length > 0 ? ((userNoAnswer / sorted.length) * 100).toFixed(1) : 0,
      };

      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1], curr = sorted[i];
        if (prev.order?.id === curr.order?.id) continue;

        const diff = (new Date(curr.createdAt) - new Date(prev.createdAt)) / 1000;

        if (diff < 30) {
          let sev, label, col;
          if (diff < 10) {
            sev = 'critical'; label = '🔴 حرج'; col = '#dc2626';
            userStats[uid].critical++;
          } else if (diff < 20) {
            sev = 'high'; label = '🟠 عالي'; col = '#ea580c';
            userStats[uid].high++;
          } else {
            sev = 'medium'; label = '🟡 متوسط'; col = '#d97706';
            userStats[uid].medium++;
          }
          userStats[uid].suspicious++;

          suspiciousCalls.push({
            id: curr.id, oderId: uid, userName: data.name, diffSeconds: diff,
            severity: sev, severityLabel: label, severityColor: col,
            prevLog: prev, currLog: curr,
            prevTime: new Date(prev.createdAt), currTime: new Date(curr.createdAt)
          });
        }
      }
    });

    suspiciousCalls.sort((a, b) => a.diffSeconds - b.diffSeconds);
    return { suspiciousCalls, userStats };
  }, [logs]);

  const filtered = useMemo(() => {
    return analysis.suspiciousCalls.filter(c => {
      if (filterUser !== 'all' && c.oderId !== filterUser) return false;
      if (filterSeverity !== 'all' && c.severity !== filterSeverity) return false;
      return true;
    });
  }, [analysis.suspiciousCalls, filterUser, filterSeverity]);

  const fmt = d => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const totals = Object.values(analysis.userStats).reduce((a, u) => ({ c: a.c + u.critical, h: a.h + u.high, m: a.m + u.medium }), { c: 0, h: 0, m: 0 });

  const th = { padding: '14px 12px', textAlign: 'right', fontWeight: '600', fontSize: '13px', color: '#475569', borderBottom: '2px solid #e2e8f0' };
  const td = { padding: '14px 12px', textAlign: 'right', fontSize: '13px', borderBottom: '1px solid #f1f5f9' };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* Status Stats - معدلات الحالات */}
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
          📈 معدلات الحالات
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {/* تأكيد */}
          <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '20px', border: '2px solid #bbf7d0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#166534', fontWeight: '600' }}>✅ تأكيد</span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>{statusStats.confirmedPct}%</span>
            </div>
            <div style={{ fontSize: '13px', color: '#166534' }}>{statusStats.confirmed} من {statusStats.total}</div>
            <div style={{ marginTop: '10px', background: '#dcfce7', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
              <div style={{ width: `${statusStats.confirmedPct}%`, height: '100%', background: '#16a34a', borderRadius: '4px' }}></div>
            </div>
          </div>
          
          {/* إلغاء */}
          <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '20px', border: '2px solid #fecaca' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#991b1b', fontWeight: '600' }}>❌ إلغاء</span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: '#dc2626' }}>{statusStats.cancelledPct}%</span>
            </div>
            <div style={{ fontSize: '13px', color: '#991b1b' }}>{statusStats.cancelled} من {statusStats.total}</div>
            <div style={{ marginTop: '10px', background: '#fee2e2', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
              <div style={{ width: `${statusStats.cancelledPct}%`, height: '100%', background: '#dc2626', borderRadius: '4px' }}></div>
            </div>
          </div>
          
          {/* لا يرد */}
          <div style={{ background: '#fffbeb', borderRadius: '12px', padding: '20px', border: '2px solid #fde68a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '600' }}>📵 لا يرد</span>
              <span style={{ fontSize: '24px', fontWeight: '700', color: '#d97706' }}>{statusStats.noAnswerPct}%</span>
            </div>
            <div style={{ fontSize: '13px', color: '#92400e' }}>{statusStats.noAnswer} من {statusStats.total}</div>
            <div style={{ marginTop: '10px', background: '#fef3c7', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
              <div style={{ width: `${statusStats.noAnswerPct}%`, height: '100%', background: '#d97706', borderRadius: '4px' }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Suspicious Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <StatCard title="إجمالي السجلات" value={logs.length} icon="📋" color="#3b82f6" />
        <StatCard title="مشبوهة" value={analysis.suspiciousCalls.length} icon="⚠️" color="#f59e0b"
          subtitle={logs.length > 0 ? `${((analysis.suspiciousCalls.length / logs.length) * 100).toFixed(1)}%` : '0%'} />
        <StatCard title="حرجة (<10s)" value={totals.c} icon="🔴" color="#dc2626" />
        <StatCard title="عالية (10-20s)" value={totals.h} icon="🟠" color="#ea580c" />
        <StatCard title="متوسطة (20-30s)" value={totals.m} icon="🟡" color="#d97706" />
      </div>

      {/* User Summary */}
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '16px', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
          👥 ملخص المستخدمين
        </h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['المستخدم', 'الإجراءات', '✅ تأكيد', '❌ إلغاء', '📵 لا يرد', 'مشبوهة', '🔴', '🟠', '🟡'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(analysis.userStats)
                .sort((a, b) => b[1].total - a[1].total)
                .map(([id, s]) => {
                  return (
                    <tr key={id}>
                      <td style={td}>{s.name}</td>
                      <td style={td}>{s.total}</td>
                      <td style={{ ...td, color: '#16a34a', fontWeight: '600' }}>{s.confirmed} ({s.confirmedPct}%)</td>
                      <td style={{ ...td, color: '#dc2626', fontWeight: '600' }}>{s.cancelled} ({s.cancelledPct}%)</td>
                      <td style={{ ...td, color: '#d97706', fontWeight: '600' }}>{s.noAnswer} ({s.noAnswerPct}%)</td>
                      <td style={td}>{s.suspicious}</td>
                      <td style={{ ...td, color: '#dc2626', fontWeight: '700' }}>{s.critical}</td>
                      <td style={{ ...td, color: '#ea580c', fontWeight: '600' }}>{s.high}</td>
                      <td style={{ ...td, color: '#d97706' }}>{s.medium}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filters */}
      <div style={{ background: '#f8fafc', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontWeight: '600', color: '#475569' }}>🔍 فلترة:</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#64748b', fontSize: '13px' }}>المستخدم:</label>
          <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#fff' }}>
            <option value="all">الكل</option>
            {Object.entries(analysis.userStats).map(([id, s]) => (
              <option key={id} value={id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ color: '#64748b', fontSize: '13px' }}>الخطورة:</label>
          <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', background: '#fff' }}>
            <option value="all">الكل</option>
            <option value="critical">🔴 حرج</option>
            <option value="high">🟠 عالي</option>
            <option value="medium">🟡 متوسط</option>
          </select>
        </div>
        <span style={{ color: '#94a3b8', fontSize: '13px' }}>عرض {filtered.length} من {analysis.suspiciousCalls.length}</span>
      </div>

      {/* Suspicious Calls */}
      <div style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
        <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', color: '#1e293b' }}>⚠️ الاتصالات المشبوهة</h3>
          <span style={{ background: '#fee2e2', color: '#dc2626', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' }}>
            {filtered.length}
          </span>
        </div>

        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
            <p>لا توجد اتصالات مشبوهة</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fef2f2' }}>
                  {['#', 'المستخدم', 'الخطورة', 'الفرق', 'الطلب السابق', 'حالته', 'الطلب الحالي', 'حالته'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, i) => (
                  <tr key={c.id} style={{
                    background: c.severity === 'critical' ? '#fef2f2' : c.severity === 'high' ? '#fff7ed' : '#fff'
                  }}>
                    <td style={td}>{i + 1}</td>
                    <td style={td}>{c.userName}</td>
                    <td style={{ ...td, color: c.severityColor, fontWeight: '600' }}>{c.severityLabel}</td>
                    <td style={{ ...td, fontWeight: '700', color: c.severityColor, fontSize: '16px' }}>{c.diffSeconds.toFixed(0)}s</td>
                    <td style={td}>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{fmt(c.prevTime)}</div>
                      <div style={{ fontWeight: '500' }}>#{c.prevLog.order?.id}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{c.prevLog.order?.name}</div>
                    </td>
                    <td style={td}><StatusBadge status={c.prevLog.status} /></td>
                    <td style={td}>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>{fmt(c.currTime)}</div>
                      <div style={{ fontWeight: '500' }}>#{c.currLog.order?.id}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{c.currLog.order?.name}</div>
                    </td>
                    <td style={td}><StatusBadge status={c.currLog.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '20px', marginTop: '24px', border: '1px solid #bfdbfe' }}>
        <h4 style={{ margin: '0 0 12px', fontSize: '14px', color: '#1e40af' }}>💡 منطق التحليل:</h4>
        <ul style={{ margin: 0, paddingRight: '20px', lineHeight: '1.8', color: '#1e40af', fontSize: '13px' }}>
          <li>قياس الفترة بين <strong>طلبات مختلفة</strong> لنفس المستخدم</li>
          <li>استبعاد النقرات المتكررة على نفس الطلب</li>
          <li><span style={{ color: '#dc2626' }}>🔴 حرج (&lt;10s)</span> = شبه مؤكد لم يتصل</li>
          <li><span style={{ color: '#ea580c' }}>🟠 عالي (10-20s)</span> = غير كافي للاتصال</li>
          <li><span style={{ color: '#d97706' }}>🟡 متوسط (20-30s)</span> = مشبوه</li>
        </ul>
      </div>
    </div>
  );
}

// المكون الرئيسي
export default function CallVerificationReport() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('logs');
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      setLogs(data.logs || []);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error fetching logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const fmtDate = d => d.toLocaleDateString('en-GB') + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  const tabStyle = (isActive) => ({
    padding: '14px 28px',
    border: 'none',
    background: isActive ? '#fff' : '#e2e8f0',
    color: isActive ? '#6366f1' : '#64748b',
    fontWeight: '600',
    fontSize: '15px',
    cursor: 'pointer',
    borderRadius: '12px 12px 0 0',
    marginLeft: '4px',
    transition: 'all 0.2s'
  });

  return (
    <div dir="rtl" style={{
      fontFamily: "'Segoe UI', Tahoma, sans-serif",
      background: '#f1f5f9',
      minHeight: '100vh',
      padding: '24px'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        borderRadius: '16px',
        padding: '28px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(99, 102, 241, 0.25)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '26px', fontWeight: '700', color: '#fff', display: 'flex', alignItems: 'center', gap: '12px' }}>
            📞 سجل الاتصالات
          </h1>
          <p style={{ margin: '8px 0 0', color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>
            متابعة وتحليل اتصالات الموظفين
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {lastUpdate && (
            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)' }}>
              آخر تحديث: {fmtDate(lastUpdate)}
            </span>
          )}
          <button
            onClick={fetchLogs}
            disabled={loading}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '2px solid rgba(255,255,255,0.3)',
              padding: '10px 20px',
              borderRadius: '10px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: loading ? 0.6 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            🔄 {loading ? 'جاري التحديث...' : 'تحديث'}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <ErrorMessage message={error} onRetry={fetchLogs} />}

      {/* Tabs */}
      {!error && (
        <>
          <div style={{ display: 'flex' }}>
            <button style={tabStyle(activeTab === 'logs')} onClick={() => setActiveTab('logs')}>
              📋 سجل التأكيد
            </button>
            <button style={tabStyle(activeTab === 'reports')} onClick={() => setActiveTab('reports')}>
              📊 التقارير
            </button>
          </div>

          <div style={{
            background: '#fff',
            borderRadius: '0 12px 12px 12px',
            padding: '24px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0'
          }}>
            {activeTab === 'logs' && <LogsTab logs={logs} loading={loading} />}
            {activeTab === 'reports' && <ReportsTab logs={logs} loading={loading} />}
          </div>
        </>
      )}
    </div>
  );
}